import { useCallback, useEffect, useState, useRef } from 'react'
import { getKyousouApiClient } from '../services/network'
import { EnterpriseSummary } from '../clients/kyousou_api_client'
import {
  EnterpriseSortType,
  EnterpriseSortTypeValue,
} from '../types/enterprise'

/**
 * お気に入りの企業一覧を取得するフック（無限スクロール対応）
 */
export const useOwnFavorite = (
  limit = 9,
  // TODO: マッチング率ソートはパフォーマンス改善後に有効化
  // initialSort: EnterpriseSortTypeValue = EnterpriseSortType.MATCHING_RATE
  initialSort: EnterpriseSortTypeValue = EnterpriseSortType.CREATED_AT
): {
  totalCount: number
  cursor: number
  isLoading: boolean
  isLoadingMore: boolean // 追加ローディング中かどうか
  hasMore: boolean // 追加読み込み可能かどうか
  enterprises: EnterpriseSummary[]
  loadMore: () => void
  changeSort: (newSort: EnterpriseSortTypeValue) => void
  currentSort: EnterpriseSortTypeValue
} => {
  const apiClient = getKyousouApiClient()
  const [totalCount, setTotalCount] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [enterprises, setEnterprises] = useState<EnterpriseSummary[]>([])
  const [currentSort, setCurrentSort] = useState(initialSort)
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasMore = cursor < totalCount

  const load = useCallback(
    async (isLoadMore = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }

      try {
        const response = await apiClient.enterprise.favoriteListList({
          cursor: isLoadMore ? cursor : 0,
          limit,
          sort: currentSort,
        })
        const newEnterprises = response.data.enterprises
        setTotalCount(response.data.total)

        if (!abortControllerRef.current?.signal.aborted) {
          setEnterprises((prevEnterprises) => {
            if (!isLoadMore) return newEnterprises
            return [...prevEnterprises, ...newEnterprises]
          })

          setCursor((prev) =>
            isLoadMore ? prev + newEnterprises.length : newEnterprises.length
          )
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('お気に入り企業の取得に失敗しました:', error)
          if (!isLoadMore) {
            setEnterprises([])
            setTotalCount(0)
            setCursor(0)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, currentSort, cursor]
  )

  useEffect(() => {
    load(false)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [currentSort])

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    load(true)
  }, [isLoadingMore, hasMore, isLoading, load])

  const changeSort = useCallback(
    (newSort: EnterpriseSortTypeValue) => {
      if (newSort !== currentSort) {
        setCurrentSort(newSort)
        setEnterprises([])
        setTotalCount(0)
        setCursor(0)
      }
    },
    [currentSort]
  )

  return {
    totalCount,
    cursor,
    isLoading,
    isLoadingMore,
    hasMore,
    enterprises,
    loadMore,
    changeSort,
    currentSort,
  }
}
